import React, { useState } from 'react';
import { editRequestsAPI } from '../../services/api';

function EditRequestModal({ lead, onClose, onSuccess }) {
  const [reason, setReason] = useState('');
  const [proposedChanges, setProposedChanges] = useState({
    schoolName: lead.schoolName || '',
    personName: lead.personName || '',
    designation: lead.designation || '',
    email: lead.email || '',
    phoneNumber: lead.phoneNumber || '',
    address: lead.address || '',
    expectedValue: lead.expectedValue || 0,
    source: lead.source || 'Website',
    statusDescription: lead.statusDescription || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const LeadSourceOptions = [
    { name: 'Website', value: 'Website' },
    { name: 'Event', value: 'Event' },
    { name: 'Referral', value: 'Referral' },
    { name: 'Direct Outreach', value: 'Direct Outreach' },
    { name: 'Marketing Campaign', value: 'Marketing Campaign' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError('Please provide a reason for the edit request');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await editRequestsAPI.createEditRequest({
        leadId: lead._id,
        reason,
        proposedChanges
      });

      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit edit request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold">Request Edit Permission</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-4">
            <p className="text-sm text-gray-600">
              Lead: <span className="font-semibold">{lead.schoolName}</span>
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Edit Request *
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Please explain why you need to edit this lead..."
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-24"
                required
              />
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Proposed Changes:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">School Name</label>
                  <input
                    type="text"
                    value={proposedChanges.schoolName}
                    onChange={(e) => setProposedChanges({...proposedChanges, schoolName: e.target.value})}
                    className="w-full p-2 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                  <input
                    type="text"
                    value={proposedChanges.personName}
                    onChange={(e) => setProposedChanges({...proposedChanges, personName: e.target.value})}
                    className="w-full p-2 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                  <input
                    type="text"
                    value={proposedChanges.designation}
                    onChange={(e) => setProposedChanges({...proposedChanges, designation: e.target.value})}
                    className="w-full p-2 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={proposedChanges.email}
                    onChange={(e) => setProposedChanges({...proposedChanges, email: e.target.value})}
                    className="w-full p-2 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input
                    type="tel"
                    value={proposedChanges.phoneNumber}
                    onChange={(e) => setProposedChanges({...proposedChanges, phoneNumber: e.target.value})}
                    className="w-full p-2 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    value={proposedChanges.address}
                    onChange={(e) => setProposedChanges({...proposedChanges, address: e.target.value})}
                    className="w-full p-2 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expected Value</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={proposedChanges.expectedValue}
                    onChange={(e) => setProposedChanges({...proposedChanges, expectedValue: parseFloat(e.target.value) || 0})}
                    className="w-full p-2 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <select
                    value={proposedChanges.source}
                    onChange={(e) => setProposedChanges({...proposedChanges, source: e.target.value})}
                    className="w-full p-2 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {LeadSourceOptions.map((source) => (
                      <option key={source.value} value={source.value}>
                        {source.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status Description</label>
                  <input
                    type="text"
                    value={proposedChanges.statusDescription}
                    onChange={(e) => setProposedChanges({...proposedChanges, statusDescription: e.target.value})}
                    className="w-full p-2 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Optional description for current status"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Submit Request'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditRequestModal;